<template>
    <div class="for-screen">
        <page-title
            :title="`${$student_label()}アカウント管理`"
            icon="bi-card-text"
            :backTo="{ name: 'PageKumiiroHome' }"
        />

        <inline-loader v-if="loading.license"></inline-loader>

        <div v-else>
            <div v-if="licenses.length">
                <section-title
                    :title="`${Grade.getGradeValue(selected.grade)}（${selected.year}年度）`"
                ></section-title>
                <ul class="nav nav-tabs nav-pills mt-3 mb-3" id="pills-tab" role="tablist">
                    <li
                        class="nav-item"
                        role="presentation"
                        v-for="school_class in school_classes"
                        :key="school_class"
                    >
                        <button
                            class="nav-link rounded-0 rounded-top px-md-5 ms-1 ms-md-2"
                            :class="{'active': selected.school_class.school_class_id === school_class.school_class_id}"
                            id="pills-1-tab"
                            data-bs-toggle="pill"
                            type="button"
                            role="tab"
                            @click="
                                getAccountUsers(school_class);
                                setSchoolClass(school_class);"
                        >
                            {{ school_class.class_name_display }}
                        </button>
                    </li>
                </ul>

                <div class="tab-content">
                    <inline-loader v-if="loading.account_user"></inline-loader>
                    <div class="mb-5" v-else>
                        <summary-box title="詳細">
                            <div class="d-flex">
                                <div class="col-10 align-self-center">
                                    <summary-box-row
                                        title="アカウント数"
                                        :text="selected.license.account_number"
                                        optional_title_class="col-6"
                                    ></summary-box-row>
                                    <summary-box-row
                                        title="有効期限"
                                        :text="selected.license.available_term_display"
                                        optional_title_class="col-6"
                                    ></summary-box-row>
                                </div>
                                <div class="col-14 align-self-start">
                                    <section-title-sub :title="`${$student_label()}情報一括登録`" class="pe-4"></section-title-sub>
                                    <div class="text-success">
                                        {{ $student_label() }}情報を一括登録する場合は、最初に{{ $student_label() }}情報をダウンロードし、<br>
                                        そのファイルを修正してインポートしてください。<br>
                                        変更できる箇所は、「出席番号」「{{ $student_label() }}名」「ログイン許可」の欄です。<br>
                                        「ID」は変更しないように注意してください。
                                    </div>
                                    <div class="mt-4 d-flex">
                                        <button-exec-view
                                            icon="bi-download"
                                            text="生徒情報ダウンロード"
                                            size="btn-sm"
                                            class="me-5"
                                            @click="downloadCsv()"
                                        ></button-exec-view>
                                        <button-exec-view
                                            icon="bi-upload"
                                            text="生徒情報インポート"
                                            size="btn-sm"
                                            class="me-5"
                                            @click="openModal()"
                                        ></button-exec-view>
                                        <button-exec-view
                                            text="QRコード一括印刷"
                                            size="btn-sm"
                                            @click="printAllQrCode()"
                                        ></button-exec-view>
                                    </div>
                                </div>

                            </div>
                        </summary-box>
                        <div>
                            <form @submit.prevent="save" class="mt-5" v-if="account_users.length">
                                <table-normal class="table">
                                    <thead>
                                        <tr>
                                            <th class="text-center">出席番号</th>
                                            <th class="text-center">{{ $student_label() }}名</th>
                                            <th class="text-center">ログイン許可</th>
                                            <th class="text-center"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(user) in account_users" :key="user">
                                            <td class="text-center">
                                                <input type="number" v-model="user.student_number" class="form-control" min="1" max="99">
                                            </td>
                                            <td class="text-center">
                                                <input type="text" v-model="user.student_name" class="form-control">
                                                </td>
                                            <td class="text-center">
                                                <form-select
                                                    v-model="user.is_pending"
                                                    :options="is_pending"
                                                    empty_option="-- 選択 --"
                                                />
                                            </td>
                                            <td class="d-flex justify-content-around">
                                                <button-exec-remove
                                                    icon="bi-qr-code"
                                                    text="QRリセット"
                                                    :disabled="user.is_pending == IsPending.PENDING"
                                                    @click="setAccountUser(user); $refs.confirm_reset.show();"
                                                    size="btn-md"
                                                ></button-exec-remove>
                                                <button-exec-view
                                                    text="印刷"
                                                    :disabled="user.is_pending == IsPending.PENDING"
                                                    @click="printQrCode(user)"
                                                    size="btn-md"
                                                ></button-exec-view>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table-normal>
                                <div class="mt-5">
                                    <button-wrap>
                                        <template v-slot:default>
                                            <button-exec-edit :type="'submit'" />
                                        </template>
                                    </button-wrap>
                                </div>
                            </form>
                            <alert-no-record text="ライセンスに紐付けられたアカウントはありません" class="mt-5" v-else></alert-no-record>
                        </div>
                    </div>
                </div>
            </div>
            <alert-no-record text="選択された年度・学年にはライセンスがありません" v-else></alert-no-record>
        </div>
    </div>

    <!-- 生徒情報インポートモーダル -->
    <div v-if="modal_shown">
        <transition name="fade">
            <div class="modal_inner">
                <div class="modal-backdrop show"></div>
                <div class="modal" tabindex="-1" style="display:block">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <!-- modal head -->
                            <div class="modal-header">
                                <h4 class="modal-title">生徒情報インポート</h4>
                                <button type="button" class="btn-close" @click="closeModal"></button>
                            </div>
                            <!-- modal body -->
                            <div class="modal-body">
                                <div class="d-flex justify-content-center">
                                    <div class="me-4">
                                        <input
                                            type="file"
                                            accept=".csv"
                                            class="form-control"
                                            @change="displayUploadCsvData"
                                        >
                                    </div>
                                </div>

                                <div class="mt-5">
                                    <form @submit.prevent="saveImport" v-if="imported_account_users.length">
                                        <text-info>
                                            下記の内容で良ければ保存を押してください。内容を書き換えると修正もできます
                                        </text-info>
                                        <table-normal class="table">
                                            <thead>
                                                <tr>
                                                    <th class="text-center">出席番号</th>
                                                    <th class="text-center">{{ $student_label() }}名</th>
                                                    <th class="text-center">ログイン許可</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(user) in imported_account_users" :key="user">
                                                    <td class="text-center">
                                                        <input type="number" v-model="user.student_number" class="form-control" min="1" max="99" required>
                                                    </td>
                                                    <td class="text-center">
                                                        <input type="text" v-model="user.student_name" class="form-control" required>
                                                        </td>
                                                    <td class="text-center">
                                                        <form-select
                                                            v-model="user.is_pending"
                                                            :options="is_pending"
                                                            empty_option="-- 選択 --"
                                                            :required="true"
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table-normal>
                                        <div class="mt-5">
                                            <button-wrap>
                                                <template v-slot:default>
                                                    <button-exec-edit :type="'submit'" />
                                                </template>
                                            </button-wrap>
                                        </div>
                                    </form>
                                    <alert-no-record text="生徒情報をインポートしてください" v-else></alert-no-record>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>

    <confirm-dialog ref="confirm_reset" @ok="resetQrCode">
        <p>QRコードをリセットしてもよろしいですか？</p>
    </confirm-dialog>
    <!-- 生徒ごとのQRコード印刷 -->
    <div class="for-print position-relative" :class="{'active': print_all === 0}">
        <div class="d-flex justify-content-center print-mt">
            <div class="border border-dark qr_card" style="border-radius: 25px">
                <div class="school_name">
                    {{ school.school_name }}
                </div>
                <div class="class_student_number">
                    <div>
                        {{ Grade.getWhereSchoolType(school.school_type, selected.grade) }}
                        {{ selected.school_class.class_name_display }}
                    </div>
                    <div>
                        <div v-show="selected.account_user.student_number">
                            {{ selected.account_user.student_number }}番
                        </div>
                    </div>
                </div>
                <div class="student_name">
                    {{ selected.account_user.student_name ?? ' ' }}
                </div>

                <div class="qr_code">
                    <img :src="qr_code_url" id="qr_image" alt="" title=""/>
                </div>
            </div>

        </div>
    </div>

    <!-- 生徒全員のQRコード印刷 -->
    <div class="d-flex flex-wrap">
        <template v-for="(account_user_url, index) in account_user_qr_code_urls" :key="account_user_url.account_user_id">
            <div :class="(index +1) % 4 === 0 ? 'page-break-after' : ''">
                <div class="for-print" :class="{'active': print_all === 1}">
                    <div class="print-mt-all">
                        <div class="border border-dark qr_card" style="border-radius: 25px" >
                            <div class="school_name">
                                {{ school.school_name }}
                            </div>
                            <div class="class_student_number">
                                <div>
                                    {{ Grade.getWhereSchoolType(school.school_type, selected.grade) }}
                                    {{ selected.school_class.class_name_display }}
                                </div>
                                <div>
                                    <div v-if="account_user_url" v-show="account_user_url.account_user.student_number">
                                        {{ account_user_url.account_user.student_number }}番
                                    </div>
                                </div>
                            </div>
                            <div class="student_name">
                                {{ account_user_url.account_user ? account_user_url.account_user.student_name : ' ' }}
                            </div>
                            <div class="qr_code">
                                <img :src="account_user_url.url" class="all_users_qr_image"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { downloadCsv } from '@/shared/utilities/download';
import ConfirmDialog from '@/shared/components/tool/ConfirmDialog.vue';
import TextInfo from '@/shared/components/design/TextInfo';
import PageTitle from '@/shared/components/design/PageTitle.vue';
import SectionTitle from '@/shared/components/design/SectionTitle.vue';
import SectionTitleSub from '@/shared/components/design/SectionTitleSub';
import AlertNoRecord from '@/shared/components/design/AlertNoRecord.vue';
import FormSelect from '@/shared/components/form/FormSelect.vue';
import InlineLoader from '@/shared/components/tool/InlineLoader';
import SummaryBox from '@/shared/components/design/SummaryBox.vue';
import SummaryBoxRow from '@/shared/components/design/SummaryBoxRow.vue';
import TableNormal from '@/shared/components/design/TableNormal.vue';
import ButtonWrap from '@/shared/components/design/buttons/ButtonWrap.vue';
import ButtonExecRemove from '@/shared/components/design/buttons/ButtonExecRemove.vue';
import ButtonExecEdit from '@/shared/components/design/buttons/ButtonExecEdit.vue';
import ButtonExecView from '@/shared/components/design/buttons/ButtonExecView.vue';
import AccountUser from '@/shared/models/entities/account-user';
import License from '@/shared/models/entities/license';
import School from '@/shared/models/entities/school';
import SchoolClass from '@/shared/models/entities/school-class';
import IsPending from '@/shared/models/enums/is-pending';
import Grade from '@/shared/models/enums/grade';

export default {
    name: 'PageKumiiroSettingStudentList',
    components: {
        ConfirmDialog,
        TextInfo,
        PageTitle,
        SectionTitle,
        SectionTitleSub,
        AlertNoRecord,
        InlineLoader,
        SummaryBox,
        SummaryBoxRow,
        FormSelect,
        TableNormal,
        ButtonExecRemove,
        ButtonExecEdit,
        ButtonExecView,
        ButtonWrap,
    },
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage'
    ],
    data() {
        return {
            //部分ローダー
            loading: {
                license: false,
                account_user: false,
            },

            //enums
            IsPending: IsPending,
            Grade: Grade,

            //選択肢
            school_classes: [],
            is_pending: IsPending.optionForSchool(),

            selected: {
                //対象年度
                year: parseInt(this.$route.query.year, 10),
                //対象学年
                grade: parseInt(this.$route.query.grade, 10),
                //対象クラス
                school_class: new SchoolClass(),
                //対象ライセンス
                license: new License(),
                //QRコードリセット対象のアカウントユーザー
                account_user: new AccountUser(),
            },

            //表示データ
            //対象年度・対象学年のライセンス一覧
            licenses: [],

            //対象年度・対象学年・選択されたクラスのアカウントユーザー一覧
            account_users: [],

            //学校情報
            school: new School(this.$store.state.school.auth.school),

            //QRコードURL
            qr_code_url: null, //一人印刷
            print_all: null, //印刷フラグ
            account_user_qr_code_urls: [], //児童アカウントとURLの配列

            //生徒情報インポートモーダル
            modal_shown: false,
            imported_account_users: [],

            unloaded: 0,
        }
    },
    mounted() {
        this.loading.license = true;

        //パラメータ（学年・クラス）がないときの処理
        if (
            this.$helper.isNud(parseInt(this.$route.query.year, 10)) ||
            this.$helper.isNud(parseInt(this.$route.query.grade, 10))
            )
        {
            this.loading.license = false;
            return;
        }

        //ライセンス情報取得
        this.$http.get(`/school/kumiiro/year/${parseInt(this.$route.query.year, 10)}/grade/${parseInt(this.$route.query.grade, 10)}/license`)
        .then(response => {
            for (let row of response.data) {
                this.licenses.push(new License(row));
            }

            if (this.licenses.length) {
                //クラスの値生成
                for (let license of this.licenses) {
                    this.school_classes.push(license.school_class);
                }
                //クラス選択ボタンの初期値
                this.selected.school_class = this.school_classes[0];

                //該当クラスの児童一覧取得
                this.getAccountUsers(this.selected.school_class);
            }
        })
        .finally(() => {
            this.loading.license = false;
        });
    },
    watch: {
        'selected.school_class'() {
            this.selected.license = this.licenses.find(license => {
                return license.school_class.school_class_id === this.selected.school_class.school_class_id;
            });
        },
        qr_code_url() {

        }
    },
    methods: {
        //選択中のクラスをセット
        setSchoolClass(school_class) {
            this.selected.school_class = school_class;
        },
        //該当クラスの児童一覧取得
        getAccountUsers(school_class) {
            this.loading.account_user = true;

            //アカウントユーザーリセット
            this.account_users.splice(0);

            this.$http.get(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${school_class.school_class_id}/account_user`)
            .then(response => {
                for (let row of response.data) {
                    this.account_users.push(new AccountUser(row));
                }
            })
            .finally(() => {
                this.loading.account_user = false;
            });
        },
        //対象のアカウントユーザー編集
        save() {
            this.startScreenLoading();
            this.$http.put(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${this.selected.license.school_class.school_class_id}/account_user`, {
                account_users: this.account_users
            })
            .then(() => {
                this.showMessage('更新しました');
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        //QRリセット対象のアカウントユーザーをセット
        setAccountUser(account_user = null) {
            this.selected.account_user = account_user;
        },
        //QRコードリセット
        resetQrCode() {
            this.startScreenLoading();
            this.$http.post(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${this.selected.license.school_class.school_class_id}/account_user/${this.selected.account_user.account_user_id}/qr_reset`)
            .then(() => {
                this.showMessage('QRコードをリセットしました');
            })
            .finally(() => {
                //ターゲット初期化
                this.setAccountUser();
                this.endScreenLoading();
            });
        },
        //QRコード印刷
        printQrCode(user) {
            this.startScreenLoading();

            this.$http.get(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${this.selected.school_class.school_class_id}/account_user/${user.account_user_id}/qr_info`)
            .then(response => {
                this.setAccountUser(response.data);

                let login_uri = encodeURIComponent(`${process.env.VUE_APP_WEB_BASE_URI}/kumiiro/signin?login_key=${this.selected.account_user.login_key}&login_code=${this.selected.account_user.login_code}`);
                this.qr_code_url = `https://api.qrserver.com/v1/create-qr-code/?data=${login_uri}`;

                //印刷プレビュー表示
                this.print_all = 0;
                let print = () => {
                    window.print();
                }
                document.getElementById('qr_image').addEventListener('load', print);

                //印刷orキャンセル後
                let afterPrint = () => {
                    document.getElementById('qr_image').removeEventListener('load', print);
                    window.removeEventListener('afterprint', afterPrint);
                    this.qr_code_url = null;
                }
                window.addEventListener('afterprint', afterPrint);
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        //QRコード一括印刷
        printAllQrCode() {
            this.startScreenLoading();

            this.$http.get(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${this.selected.school_class.school_class_id}/all_qr_info`)
            .then(response => {

                //リセット
                this.account_user_qr_code_urls.splice(0);

                for (let account_user of response.data) {
                    let login_uri = encodeURIComponent(`${process.env.VUE_APP_WEB_BASE_URI}/kumiiro/signin?login_key=${account_user.login_key}&login_code=${account_user.login_code}`);
                    this.account_user_qr_code_urls.push({
                        account_user: account_user,
                        url: `https://api.qrserver.com/v1/create-qr-code/?data=${login_uri}`,
                    });
                }

                this.unloaded = this.account_user_qr_code_urls.length;

                //印刷プレビュー表示
                this.print_all = 1; //一括印刷フラグをON（表示を切り替え
                this.$nextTick(() => {
                    let all_print = () => {
                        this.unloaded--;
                        if (this.unloaded === 0) {
                            window.print();
                        }
                    }

                    let images = document.getElementsByClassName('all_users_qr_image')
                    for (let i = 0; i < images.length; i++) {
                        images[i].addEventListener('load', all_print);
                    }


                    //印刷orキャンセル後
                    let afterPrint = () => {
                       for (let i = 0; i < images.length; i++){
                            images[i].removeEventListener('load', all_print);
                       }
                        window.removeEventListener('afterprint', afterPrint);
                        for(let account_user_url of this.account_user_qr_code_urls){
                            account_user_url.url = null;
                        }
                    }
                    window.addEventListener('afterprint', afterPrint);
                })
                .finally(() => {
                    this.endScreenLoading();
                })
            });
        },
        //生徒情報インポートモーダルオープン
        openModal() {
            this.modal_shown = true;
        },
        //生徒情報インポートモーダルクローズ
        closeModal() {
            this.modal_shown = false;
        },
        //生徒情報ダウンロード
        downloadCsv() {
            let header;
            let records;
            this.startScreenLoading();
            this.$http.get(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${this.selected.school_class.school_class_id}/account_user/csv/download`)
            .then(response => {
                header = Object.keys(response.data[0]);
                records = response.data.map((record) => {
                    return Object.values(record);
                });
                downloadCsv(header, records, `${this.Grade.getGradeValue(this.selected.grade)}${this.selected.school_class.class_name_display}（${this.selected.year}年度）`);
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        //csvデータ取得
        displayUploadCsvData(event) {
            this.imported_account_users.splice(0);
            let csv = event.target.files[0];

            if (!csv.type.match("text/csv")) {
                this.showErrorMassage('csvファイルを選択してください');
                return;
            }

            let reader = new FileReader();
            reader.readAsText(csv, "Shift_JIS");
            reader.onload = () => {
                let lines = reader.result.split("\n");
                lines.shift();

                let lines_array = [];
                for (let i = 0; i < lines.length; i++) {
                    lines_array[i] = lines[i].split(",");
                }


                for (let line of lines_array) {
                    //何も入っていない行は飛ばす
                    if (line.length !== 4) {
                        continue;
                    }

                    //csv一行の定義
                    let imported_account_user = new AccountUser();

                    imported_account_user.account_user_id = line[0] ? parseInt(line[0], 10) : '';
                    imported_account_user.student_number = line[1] ? parseInt(line[1], 10) : '';
                    imported_account_user.student_name = line[2];
                    if (line[3].trim() === 'ログイン可') {
                        imported_account_user.is_pending = IsPending.ACTIVE;
                    } else {
                        imported_account_user.is_pending = IsPending.PENDING;
                    }

                    this.imported_account_users.push(imported_account_user);
                }
            };
        },
        //送る値のバリデーション、エラーならアラート上げる
        validatePostData() {
            let success = true;
            let alert_messages = [];

            if (this.imported_account_users.length) {
                this.imported_account_users.forEach((student, i) => {
                    if (this.$helper.isNud(student.account_user_id)) {
                        alert_messages.push((i + 1) + '列目のIDが未入力です');
                        success = false;
                    }
                    if (this.$helper.isNud(student.student_number)) {
                        alert_messages.push((i + 1) + '列目の出席番号が未入力です');
                        success = false;
                    }
                    if (this.$helper.isNud(student.student_name)) {
                        alert_messages.push((i + 1) + `列目の${this.$$student_label()}名が未入力です`);
                        success = false;
                    }
                    if (this.$helper.isNud(student.is_pending)) {
                        alert_messages.push((i + 1) + '列目のログイン許可が未入力です');
                        success = false;
                    }
                });
            } else {
                alert_messages.push(`${this.$student_label()}アカウントが少なくとも１つは必要です`);
                success = false;
            }

            if (!this.IsDuplicatedAccountUser()) {
                alert_messages.push('他のIDと重複しているIDがあります。IDは変更しないようにしてください。');
                success = false;
            }

            if (!success) {
                this.showErrorMessage('入力エラー', alert_messages.join("\n"));
                return false;
            }

            return true;
        },
        //アカウントユーザーが重複していないかチェック
        IsDuplicatedAccountUser() {
            let imported_account_user_ids = this.imported_account_users.map(user => {
                return user.account_user_id;
            })

            let filter_ids = new Set(imported_account_user_ids);
            let result = (filter_ids.size === imported_account_user_ids.length);

            return result;
        },
        //インポートされた生徒情報を保存
        saveImport() {
            this.startScreenLoading();

            //バリデーション実行
            let result = this.validatePostData();

            if (!result) {
                this.endScreenLoading();
                return;
            }

            //実行
            this.$http.put(`/school/kumiiro/year/${this.selected.year}/grade/${this.selected.grade}/class/${this.selected.license.school_class.school_class_id}/account_user/import`, {
            account_users: this.imported_account_users
            })
            .then((response) => {
                this.account_users.splice(0);
                for (let new_student of response.data) {
                    this.account_users.push(new_student);
                }
                this.showMessage('更新しました');
            })
            .finally(() => {
                this.closeModal();
                this.endScreenLoading();
            });
        },
    },

}
</script>

<style scoped>
    .for-print {display: none;}

    @page {
        size: A4;
        margin: 0;
    }

    @media print {
        /* 各生徒ごとの印刷デザイン */
        .for-screen {display: none;}
        .for-print.active {display: block;}

        .print-mt {margin-top: 50mm;}

        .print-sheet {position:relative;width:172mm !important;height:251mm !important; padding: 10mm; page-break-after: always; background: #FFF; margin: auto;}

        img {width: 35mm; height: 35mm;}

        /* 一括印刷時のデザイン */
        .print-mt-all {display: block; padding: 5mm; margin: 10mm 0 0 0}
    }

    /**
    * デザイン
    */
    .qr_card {width: 50mm; height: 80mm;}
    .school_name {text-align: center; font-size: 1rem; margin-top: 5mm;}
    .class_student_number {display:flex; justify-content: space-around; margin-top: 5mm}
    .student_name {text-align: center; border-bottom: 1px solid #000; margin:7mm 3mm 0 3mm}
    .qr_code {margin-top: 5mm; text-align: center;}
    .page-break-after {page-break-after: always;}
</style>
